import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { onInstallAvailable, triggerInstall, isInstalled, isAndroid } from '../lib/installManager';
import { track } from '@/lib/analytics';

export function AndroidInstallPrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [showManualSteps, setShowManualSteps] = useState(false);

  useEffect(() => {
    if (!isAndroid()) return;
    if (isInstalled()) return;

    const dismissed = localStorage.getItem('android_install_dismissed');
    if (dismissed) {
      const daysSince = (Date.now() - parseInt(dismissed)) / (1000 * 60 * 60 * 24);
      if (daysSince < 3) return;
    }

    let promptFired = false;
    let fallbackTimer: ReturnType<typeof setTimeout>;

    const cleanup = onInstallAvailable(available => {
      if (available) {
        promptFired = true;
        clearTimeout(fallbackTimer);
        setTimeout(() => {
          setShowPrompt(true);
          track('pwa_install_prompted', { device: 'android' });
        }, 3000);
      } else {
        setShowPrompt(false);
      }
    });

    fallbackTimer = setTimeout(() => {
      if (!promptFired) {
        setShowManualSteps(true);
      }
    }, 6000);

    return () => {
      cleanup();
      clearTimeout(fallbackTimer);
    };
  }, []);

  const handleInstall = async () => {
    setInstalling(true);
    const result = await triggerInstall();
    if (result === 'accepted') {
      setInstalled(true);
      setShowPrompt(false);
      track('pwa_install_accepted', { trigger: 'android_prompt', device: 'android' });
    } else if (result === 'dismissed') {
      setInstalling(false);
      setShowPrompt(false);
      localStorage.setItem('android_install_dismissed', Date.now().toString());
      track('pwa_install_dismissed', { trigger: 'android_prompt', device: 'android' });
    } else {
      setInstalling(false);
      setShowManualSteps(true);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('android_install_dismissed', Date.now().toString());
  };

  return (
    <AnimatePresence>
      {showPrompt && !showManualSteps && (
        <motion.div
          key="android-install-banner"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 20, stiffness: 300 }}
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 9500,
            background: '#0d0d0d',
            borderTop: '1px solid rgba(225,29,72,0.3)',
            padding: '16px 20px',
            paddingBottom: 'calc(16px + env(safe-area-inset-bottom))',
            boxShadow: '0 -8px 32px rgba(0,0,0,0.5)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12 }}>
            <div
              style={{
                width: 52,
                height: 52,
                borderRadius: 12,
                background: 'linear-gradient(135deg, #e11d48, #9333ea)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 26,
                flexShrink: 0,
              }}
            >
              🔥
            </div>
            <div style={{ flex: 1 }}>
              <p
                style={{
                  color: '#ffffff',
                  fontSize: 16,
                  fontWeight: 800,
                  margin: '0 0 2px',
                  letterSpacing: -0.3,
                }}
              >
                Install Papacito
              </p>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, margin: 0 }}>
                Fast &bull; No App Store needed
              </p>
            </div>
            <button
              onClick={handleDismiss}
              style={{
                background: 'rgba(255,255,255,0.08)',
                border: 'none',
                borderRadius: '50%',
                width: 30,
                height: 30,
                color: 'rgba(255,255,255,0.4)',
                fontSize: 14,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              ✕
            </button>
          </div>

          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            {[
              { icon: '⚡', text: 'Instant' },
              { icon: '🔔', text: 'Alerts' },
              { icon: '📵', text: 'Offline' },
            ].map(item => (
              <div
                key={item.text}
                style={{
                  flex: 1,
                  background: 'rgba(255,255,255,0.05)',
                  borderRadius: 10,
                  padding: '8px 0',
                  textAlign: 'center',
                }}
              >
                <div style={{ fontSize: 18, marginBottom: 2 }}>{item.icon}</div>
                <p
                  style={{
                    color: 'rgba(255,255,255,0.6)',
                    fontSize: 11,
                    margin: 0,
                    fontWeight: 600,
                  }}
                >
                  {item.text}
                </p>
              </div>
            ))}
          </div>

          <button
            onClick={handleInstall}
            disabled={installing}
            style={{
              width: '100%',
              background: installing
                ? 'rgba(225,29,72,0.5)'
                : 'linear-gradient(135deg, #e11d48, #ec4899)',
              color: '#ffffff',
              border: 'none',
              borderRadius: 50,
              padding: '16px 0',
              fontSize: 17,
              fontWeight: 800,
              cursor: installing ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
              boxShadow: '0 4px 20px rgba(225,29,72,0.4)',
              letterSpacing: 0.2,
            }}
          >
            {installing ? (
              <>
                <div
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    border: '2px solid rgba(255,255,255,0.3)',
                    borderTopColor: '#fff',
                    animation: 'spin 0.7s linear infinite',
                  }}
                />
                Installing...
              </>
            ) : (
              <>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="white"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                >
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Install App — Free
              </>
            )}
          </button>
        </motion.div>
      )}

      {showManualSteps && (
        <motion.div
          key="android-manual-steps"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 50 }}
          style={{
            position: 'fixed',
            bottom: 80,
            left: 16,
            right: 16,
            zIndex: 9500,
            background: '#0d0d0d',
            border: '1px solid rgba(225,29,72,0.4)',
            borderRadius: 20,
            padding: 20,
            boxShadow: '0 8px 40px rgba(0,0,0,0.8)',
          }}
        >
          <button
            onClick={() => {
              setShowManualSteps(false);
              handleDismiss();
            }}
            style={{
              position: 'absolute',
              top: 12,
              right: 12,
              background: 'rgba(255,255,255,0.08)',
              border: 'none',
              borderRadius: '50%',
              width: 28,
              height: 28,
              color: 'rgba(255,255,255,0.5)',
              fontSize: 13,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            ✕
          </button>

          <p
            style={{
              color: '#fff',
              fontSize: 16,
              fontWeight: 800,
              margin: '0 0 16px',
              paddingRight: 32,
            }}
          >
            Install Papacito 🔥
          </p>

          {[
            {
              num: 1,
              text: (
                <>
                  Tap the{' '}
                  <span
                    style={{
                      background: 'rgba(255,255,255,0.12)',
                      borderRadius: 4,
                      padding: '1px 6px',
                      fontWeight: 800,
                    }}
                  >
                    ⋮
                  </span>{' '}
                  menu at top right
                </>
              ),
            },
            {
              num: 2,
              text: (
                <>
                  Tap{' '}
                  <span style={{ color: '#e11d48', fontWeight: 800 }}>"Add to Home screen"</span>
                </>
              ),
            },
            {
              num: 3,
              text: (
                <>
                  Tap <span style={{ color: '#e11d48', fontWeight: 800 }}>"Add"</span> to confirm 🎉
                </>
              ),
            },
          ].map(step => (
            <div
              key={step.num}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                background: 'rgba(255,255,255,0.05)',
                borderRadius: 12,
                padding: '10px 14px',
                marginBottom: 8,
              }}
            >
              <div
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: '50%',
                  background: '#e11d48',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: 12,
                  fontWeight: 800,
                  flexShrink: 0,
                }}
              >
                {step.num}
              </div>
              <p style={{ color: '#ffffff', fontSize: 13, margin: 0, lineHeight: 1.4 }}>
                {step.text}
              </p>
            </div>
          ))}
        </motion.div>
      )}

      {installed && (
        <motion.div
          key="android-installed"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0 }}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10000,
            background: 'rgba(0,0,0,0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 32,
          }}
        >
          <div
            style={{
              background: '#0d0d0d',
              border: '1px solid rgba(34,197,94,0.4)',
              borderRadius: 24,
              padding: 32,
              textAlign: 'center',
              maxWidth: 320,
            }}
          >
            <div style={{ fontSize: 64, marginBottom: 16 }}>🎉</div>
            <h2 style={{ color: '#fff', fontSize: 24, fontWeight: 800, margin: '0 0 10px' }}>
              Papacito Installed!
            </h2>
            <p
              style={{
                color: 'rgba(255,255,255,0.6)',
                fontSize: 15,
                margin: '0 0 24px',
                lineHeight: 1.6,
              }}
            >
              Find the Papacito icon on your home screen and open it for the full app experience 🔥
            </p>
            <button
              onClick={() => setInstalled(false)}
              style={{
                width: '100%',
                background: '#22c55e',
                color: '#fff',
                border: 'none',
                borderRadius: 50,
                padding: '16px 0',
                fontSize: 16,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Got it! Let's go 🚀
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
